'use client';
import React, { useState, useRef, useEffect } from 'react';

interface SliderProps {
  children: React.ReactNode;
  className?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  infinite?: boolean;
  slideWidth?: number;
}

const Slider: React.FC<SliderProps> = ({
  children,
  className = '',
  autoPlay = false,
  autoPlayInterval = 3000,
  showDots = false,
  showArrows = true,
  infinite = true,
  slideWidth = 80,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const childrenArray = React.Children.toArray(children);
  const totalSlides = childrenArray.length;

  // Touch/Swipe state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Minimum distance for a swipe to be detected
  const minSwipeDistance = 50;

  useEffect(() => {
    if (autoPlay && totalSlides > 1 && !isDragging) {
      const interval = setInterval(() => {
        handleNext();
      }, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [autoPlay, autoPlayInterval, totalSlides, isDragging]);

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    const reset = currentIndex === totalSlides - 1;
    if (infinite) {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1));
    }

    setTimeout(() => setIsTransitioning(false), 1000 * (reset ? totalSlides / 2 : 1));
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    const reset = currentIndex === totalSlides - 1;
    setIsTransitioning(true);

    if (infinite) {
      setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    } else {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }

    setTimeout(() => setIsTransitioning(false), 1000 * (reset ? totalSlides / 2 : 1));
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex || isDragging) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 1000);
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || isTransitioning) return;

    const currentTouch = e.targetTouches[0].clientX;
    const diff = touchStart - currentTouch;

    // Calculate drag offset as percentage for smooth visual feedback
    const dragPercentage = (diff / window.innerWidth) * 100;
    setDragOffset(dragPercentage);
    setTouchEnd(currentTouch);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd || isTransitioning) {
      setDragOffset(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - go to next slide
      if (infinite || currentIndex < totalSlides - 1) {
        handleNext();
      }
    } else if (isRightSwipe) {
      // Swipe right - go to previous slide
      if (infinite || currentIndex > 0) {
        handlePrev();
      }
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
    setIsDragging(false);
    setDragOffset(0);
  };

  // Calculate transform with drag offset
  const getTransform = () => {
    const baseTransform = -currentIndex * slideWidth;
    const dragTransform = isDragging ? -(dragOffset * 0.8) : 0; // Scale down drag effect
    return baseTransform + dragTransform;
  };

  return (
    <div className={`relative w-full max-w-[1440px] h-full mx-auto`}>
      {/* Slider Container */}
      <div
        ref={sliderRef}
        className={`flex items-stretch transition-transform ${
          isDragging
            ? 'duration-0'
            : `duration-[${currentIndex === totalSlides - 1 ? totalSlides * 1000 : 1000}ms]`
        } ease-in-out ${className}`}
        style={{
          transform: `translateX(${getTransform()}%)`,
          width: `${100}%`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {childrenArray.map((child, index) => (
          <div key={index} style={{ width: `${slideWidth}%` }} className={`flex-shrink-0 flex-grow self-stretch`}>
            {child}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {showArrows && totalSlides > 1 && (
        <>
          <button
            onClick={handlePrev}
            disabled={!infinite && currentIndex === 0}
            className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed z-10"
            aria-label="Previous slide"
          >
            <svg
              className="w-4 h-4 sm:w-6 sm:h-6 text-gray-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={handleNext}
            disabled={!infinite && currentIndex === totalSlides - 1}
            className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed z-10"
            aria-label="Next slide"
          >
            <svg
              className="w-4 h-4 sm:w-6 sm:h-6 text-gray-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {showDots && totalSlides > 1 && (
        <div className="w-full mt-[1.875rem] flex justify-center space-x-2">
          {childrenArray.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 sm:h-3 rounded-md transition-all duration-200 ${
                index === currentIndex
                  ? 'w-12 sm:w-16 bg-[#1ca7e5] '
                  : ' w-3 sm:w-4 bg-[#1ca7e5] bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Slider;
